import { useState, useRef, useCallback } from "react";

interface UseCardInteractionReturn {
  expandedCardIds: Set<string>;
  activeCardId: string;
  setActiveCardId: React.Dispatch<React.SetStateAction<string>>;
  handleCardInteraction: (cardId: string) => void;
  markCardExpanded: (cardId: string) => void;
  collapseCard: (cardId: string) => void;
  scrollToCard: (cardId: string) => void;
  setCardRef: (id: string, el: HTMLDivElement | null) => void;
  publishRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
  resetInteraction: () => void;
}

export const useCardInteraction = (): UseCardInteractionReturn => {
  const [expandedCardIds, setExpandedCardIds] = useState<Set<string>>(
    new Set()
  );
  const [activeCardId, setActiveCardId] = useState<string>("");
  const publishRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  const handleCardInteraction = useCallback((cardId: string) => {
    setExpandedCardIds((prevIds) => {
      const newSet = new Set(prevIds);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }

      const element = publishRefs.current.get(cardId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      return newSet;
    });
  }, []);

  const markCardExpanded = useCallback((cardId: string) => {
    setExpandedCardIds((prevIds) => {
      if (prevIds.has(cardId)) return prevIds;
      const newSet = new Set(prevIds);
      newSet.add(cardId);
      return newSet;
    });
  }, []);

  const collapseCard = useCallback((cardId: string) => {
    setExpandedCardIds((prevIds) => {
      if (!prevIds.has(cardId)) return prevIds;
      const newSet = new Set(prevIds);
      newSet.delete(cardId);
      return newSet;
    });
  }, []);

  const scrollToCard = useCallback((cardId: string) => {
    const element = publishRefs.current.get(cardId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const setCardRef = useCallback((id: string, el: HTMLDivElement | null) => {
    publishRefs.current.set(id, el);
  }, []);

  const resetInteraction = useCallback(() => {
    setExpandedCardIds(new Set());
    setActiveCardId("");
  }, []);

  return {
    expandedCardIds,
    activeCardId,
    setActiveCardId,
    handleCardInteraction,
    markCardExpanded,
    collapseCard,
    scrollToCard,
    setCardRef,
    publishRefs,
    resetInteraction,
  };
};
